/**
 * utils/remoteConfig.ts
 * ---------------------
 * What: Fetches feature flags and UI ordering preferences from the `remote_config`
 * table and merges them with a local default configuration.
 * Why: Allows runtime control of app sections (cards, themes, maintenance mode)
 * without requiring a new app build.
 * Notes: Maintains backward compatibility with boolean flags while supporting
 * richer objects (enabled/order). Export also exposes `localDefaultConfig` for
 * offline or server-down scenarios.
 */
import { supabase } from '../lib/supabase';

export interface CardConfig {
  enabled: boolean;
  /** Lower numbers appear first. Undefined falls back to the component's default ordering. */
  order?: number;
}

export interface RemoteConfig {
  SettingsScreen: {
    myDetails: CardConfig;
    subscriptionStatus: CardConfig;
    reporting: CardConfig;
    displayPreferences: CardConfig;
    inAppContact: CardConfig;
    devTools: CardConfig;
    legalCompliance: CardConfig;
  };
  // ADD_START: HomeScreen config and GeneralNotification
  HomeScreen: {
    maintenance: CardConfig;
    subscriptionStatus: CardConfig;
    stats: CardConfig;
    generalNotification: CardConfig;
  };
  // ADD_END: HomeScreen config and GeneralNotification
  App: {
    maintenanceMode: boolean;
    verboseLogging: boolean;
  };
  theme: {
    lightVersion: 'v3';
    darkVersion: 'v1' | 'v2' | 'v3';
    blueNeonVersion: 'v1' | 'v2' | 'v3';
  };
  plannedMaintenance: {
    enabled: boolean;
    from?: string;
    to?: string;
    // optional order for consistency
    order?: number;
  };
  // ADD_START: generalNotification config definition
  generalNotification: {
    enabled: boolean;
    message?: string;
    order?: number;
  };
  // ADD_END
  forceLogoutVersion: number;
  [key: string]: any;
}

const defaultConfig: RemoteConfig = {
  SettingsScreen: {
    myDetails: { enabled: true, order: 1 },
    subscriptionStatus: { enabled: true, order: 2 },
    reporting: { enabled: true, order: 3 },
    displayPreferences: { enabled: true, order: 4 },
    inAppContact: { enabled: true, order: 5 },
    devTools: { enabled: __DEV__, order: 6 },
    legalCompliance: { enabled: true, order: 7 },
  },
  // ADD_START: default HomeScreen config
  HomeScreen: {
    maintenance: { enabled: true, order: 1 },
    subscriptionStatus: { enabled: true, order: 2 },
    stats: { enabled: true, order: 3 },
    generalNotification: { enabled: true, order: 4 },
  },
  // ADD_END
  App: {
    maintenanceMode: false,
    verboseLogging: __DEV__,
  },
  theme: {
    lightVersion: 'v3',
    darkVersion: 'v1',
    blueNeonVersion: 'v1',
  },
  plannedMaintenance: {
    enabled: false,
  },
  // ADD_START: default generalNotification
  generalNotification: {
    enabled: false,
    message: '',
  },
  // ADD_END
  forceLogoutVersion: 0,
};

export async function fetchRemoteConfig(): Promise<RemoteConfig> {
  try {
    const { data, error } = await supabase
      .from('remote_config')
      .select('key,value')
      .returns<{ key: string; value: any }[]>();

    if (error) throw error;
    if (!data) return defaultConfig;

    const cfg: RemoteConfig = JSON.parse(JSON.stringify(defaultConfig)); // deep clone

    data.forEach(({ key, value }) => {
      // Simple boolean values are still allowed for backward-compatibility.

      const normalise = (val: any, fallbackEnabled: boolean): CardConfig => {
        if (typeof val === 'boolean' || val === undefined) {
          return { enabled: val !== false, order: undefined };
        }
        return {
          enabled: typeof val.enabled === 'boolean' ? val.enabled : fallbackEnabled,
          order: typeof val.order === 'number' ? val.order : undefined,
        };
      };

      // Maintenance / planned maintenance handled separately below – they may be boolean or object

      if (key === 'maintenanceMode') {
        cfg.App.maintenanceMode = typeof value === 'boolean' ? value : !!value.enabled;
      } else if (key === 'verboseLogging') {
        cfg.App.verboseLogging = typeof value === 'boolean' ? value : !!value.enabled;
      } else if (key === 'plannedMaintenance') {
        cfg.plannedMaintenance = {
          enabled: typeof value === 'boolean' ? value : !!value.enabled,
          from: value.from,
          to: value.to,
        };
      } else if (key.startsWith('settings')) {
        const flag = key.replace('settings', ''); // e.g., DevTools
        const camel = flag.charAt(0).toLowerCase() + flag.slice(1); // devTools
        cfg.SettingsScreen[camel as keyof RemoteConfig['SettingsScreen']] = normalise(value, true);
      // ADD_START: home screen ordering flags
      } else if (key.startsWith('home')) {
        const flag = key.replace('home', ''); // e.g., Stats
        const camel = flag.charAt(0).toLowerCase() + flag.slice(1); // stats
        cfg.HomeScreen[camel as keyof RemoteConfig['HomeScreen']] = normalise(value, true);
      // ADD_END
      } else if (key === 'generalNotification') {
        cfg.generalNotification = {
          enabled: typeof value === 'boolean' ? value : !!value.enabled,
          message: typeof value === 'string' ? value : value?.message,
          order: typeof value?.order === 'number' ? value.order : cfg.generalNotification.order,
        };
      } else if (key === 'forceLogoutVersion') {
        cfg.forceLogoutVersion = typeof value === 'number' ? value : Number(value?.version ?? 0);
      } else if (key === 'lightThemeVersion') {
        // Light theme only supports v3 now
        cfg.theme.lightVersion = 'v3';
      } else if (key === 'darkThemeVersion') {
        cfg.theme.darkVersion = value === 'v3' ? 'v3' : value === 'v2' ? 'v2' : 'v1';
      } else if (key === 'blueNeonThemeVersion') {
        cfg.theme.blueNeonVersion = value === 'v3' ? 'v3' : value === 'v2' ? 'v2' : 'v1';
      }
    });

    return cfg;
  } catch (e) {
    console.warn('[RemoteConfig] Error fetching remote config:', (e as Error).message);
    throw e; // propagate so caller can decide offline vs server_down
  }
}

export { defaultConfig as localDefaultConfig }; 